import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from './useDebounce';

export const useSearch = (userId, apiUrl, selectedFilters, filterCombinations) => {
  const [searchTerm, setSearchTerm] = useState(() => {
    const saved = localStorage.getItem("dashboardSearchTerm");
    return saved || "";
  });

  const [activeSearchTerm, setActiveSearchTerm] = useState(() => {
    const saved = localStorage.getItem("dashboardSearchTerm");
    return saved || "";
  });

  const [searchResults, setSearchResults] = useState(() => {
    const saved = localStorage.getItem("dashboardSearchResults");
    return saved ? JSON.parse(saved) : [];
  });

  const [searchLoading, setSearchLoading] = useState(false);
  const [hasActiveSearchText, setHasActiveSearchText] = useState(false);

  const performSearch = useCallback(async (searchText) => {
    const trimmedSearch = searchText.trim();
    if (!trimmedSearch || !userId) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const hasFilters = Object.values(selectedFilters || {}).some(arr => arr?.length > 0);
      let combinedNotes = [];
      const seen = new Set();

      if (hasFilters) {
        const promises = filterCombinations.map((combo) => {
          const params = new URLSearchParams({
            pageNumber: "1",
            pageSize: "50",
            searchText: trimmedSearch,
            userId: userId,
          });
          if (combo.date) params.append("date", combo.date);
          if (combo.workspace) params.append("workspaceId", combo.workspace);
          if (combo.project) params.append("projectId", combo.project);
          if (combo.job) params.append("jobId", combo.job);
          if (combo.userName) params.append("username", combo.userName);
          if (combo.task) params.append("taskId", combo.task);
          return fetch(`${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`)
            .then(res => res.ok ? res.json() : { siteNotes: [], data: [] });
        });
        const results = await Promise.all(promises);
        results.forEach(r => {
          (r.data || r.siteNotes || []).forEach(n => {
            if (!seen.has(n.id)) {
              seen.add(n.id);
              combinedNotes.push(n);
            }
          });
        });
      } else {
        const params = new URLSearchParams({
          pageNumber: "1",
          pageSize: "50",
          searchText: trimmedSearch,
          userId: userId,
        });
        const response = await fetch(`${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`);
        if (!response.ok) throw new Error();
        const data = await response.json();
        combinedNotes = data.data || data.siteNotes || [];
      }
      setSearchResults(combinedNotes.map(n => ({
        ...n,
        userName: n.UserName || n.userName,
        documentCount: n.DocumentCount || n.documentCount || 0,
        note: n.note || n.content || "",
        workspaceId: n.WorkspaceId || n.workspaceId || null,
        workspace: n.WorkspaceName || n.workspace || null,
        projectId: n.ProjectId || n.projectId || null,
        project: n.ProjectName || n.project || null,
        jobId: n.JobId || n.jobId || null,
        job: n.JobName || n.job || null,
        taskId: n.TaskId || n.taskId || null,
        task: n.title || n.task || null,
        timeStamp: n.timeStamp || n.createdAt || null,
        date: n.date || null,

      })));
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [userId, apiUrl, selectedFilters, filterCombinations]);

  const handleSearchKeyDown = useCallback((e, onEnterCallback) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setActiveSearchTerm(searchTerm);
      localStorage.setItem("dashboardSearchTerm", searchTerm);
      if (onEnterCallback) onEnterCallback(searchTerm);
    }
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
    setSearchResults([]);
    localStorage.removeItem("dashboardSearchTerm");
    localStorage.removeItem("dashboardSearchResults");
  }, []);

  useEffect(() => {
    localStorage.setItem("dashboardSearchTerm", searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setHasActiveSearchText(activeSearchTerm.trim() !== "");
    if (activeSearchTerm) {
      localStorage.setItem("dashboardSearchTerm", activeSearchTerm);
    } else {
      localStorage.removeItem("dashboardSearchTerm");
    }
    performSearch(activeSearchTerm);
  }, [activeSearchTerm, performSearch]);

  useEffect(() => {
    localStorage.setItem("dashboardSearchResults", JSON.stringify(searchResults));
  }, [searchResults]);

  return {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    setActiveSearchTerm,
    searchResults,
    setSearchResults,
    searchLoading,
    hasActiveSearchText,
    performSearch,
    handleSearchKeyDown,
    handleClearSearch,
  };
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      performSearch(debouncedSearchTerm);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm, performSearch]);
};